import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { ApproveDirectlyProps, ApproveDirectlyActionData } from "types/modals";
import { approveDocumentDirectly, fetchDraftComment } from "components/modals/modal.actions";
import { Notification, NotificationType } from "components/common/Notification.component";
import { PATH_TO_DOCUMENT_OVERVIEW } from "components/router/paths";

const ApproveDirectly = ({ more, closeModal, loopFocus, errorCallback, initialFocusRef }: ApproveDirectlyProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const [comment, setComment] = useState("");
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);
    const [actionRunning, setActionRunning] = useState(false);

    useEffect(() => {
        dispatch(fetchDraftComment(true, more.tasks, setComment));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally run only on mount

    const handleApproveSuccess = useCallback(() => {
        closeModal();
        more.handledTaskCallback(undefined, more.navigate);
        more.navigate(PATH_TO_DOCUMENT_OVERVIEW);
    }, [more, closeModal]);

    const handleApproveError = useCallback((error: Record<string, any>) => {
        setDisplayError(error.error ?? error);
        setActionRunning(false);
    }, []);

    const handleApprove = useCallback(() => {
        setActionRunning(true);
        setDisplayError(null);

        const taskIds = more.tasks.map((task: { key: string }) => task.key);

        const data: ApproveDirectlyActionData = {
            companyId: more.companyId,
            taskIds,
            comment,
        };

        dispatch(approveDocumentDirectly(data, handleApproveSuccess, handleApproveError));
    }, [comment, more, dispatch, handleApproveSuccess, handleApproveError]);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const okEnabled = !actionRunning;

    return (
        <>
            <div className="ga-modal__content">
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    ref={initialFocusRef}
                    id="comment"
                    inputHint={translate("popUp.approve.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                    value={comment}
                    propagateValue={handleCommentChange}
                    label={translate("popUp.approve.commentLabel")}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleApprove}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default ApproveDirectly;
